import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ENV } from '../../config/env';
import toast from 'react-hot-toast';
import { supabase } from '../../config/supabaseClient';

const CheckoutSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [email, setEmail] = useState('');
  const [sendingSetup, setSendingSetup] = useState(false);
  const [sendingMagicLink, setSendingMagicLink] = useState(false);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (sessionId) {
      fetch(`${ENV.API_URL}/api/payment/verify-session?session_id=${sessionId}`)
        .then(res => res.json())
        .then(data => {
          setStatus(data.status || 'success');
          setEmail(data.email || '');
          setLoading(false);

          const paymentStatus = String(data.paymentStatus || '').toLowerCase();
          const amountTotal = Number(data.amountTotal);
          const currency = String(data.currency || '').toUpperCase();

          if (paymentStatus === 'paid' && Number.isFinite(amountTotal) && currency) {
            const fbq = (window as any).fbq as undefined | ((...args: any[]) => void);
            if (typeof fbq === 'function') {
              fbq('track', 'Purchase', {
                value: amountTotal / 100,
                currency
              });
            }
          }

          try {
            localStorage.setItem('post_reset_redirect', '/onboarding/trial');
            localStorage.setItem('trial_checkout_session_id', sessionId);
            if (data.email) {
              localStorage.setItem('trial_checkout_email', String(data.email));
            }
          } catch {}
        })
        .catch(err => {
          console.error('Error verifying session:', err);
          toast.error('There was an issue verifying your purchase');
          setStatus('error');
          setLoading(false);
        });
    } else {
      setLoading(false);
      setStatus('missing');
    }
  }, [searchParams]);

  const sendPasswordSetupLink = async () => {
    const targetEmail = String(email || '').trim();
    if (!targetEmail) {
      toast.error('Missing email address for password setup');
      return;
    }

    setSendingSetup(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(targetEmail, {
        redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
      });
      if (error) {
        throw error;
      }
      toast.success('Password setup link sent. Please check your email.');
    } catch (err: any) {
      console.error('Failed to send password setup link:', err);
      toast.error(err?.message || 'Failed to send password setup link');
    } finally {
      setSendingSetup(false);
    }
  };

  const sendMagicLink = async () => {
    const targetEmail = String(email || '').trim();
    if (!targetEmail) {
      toast.error('Missing email address');
      return;
    }

    setSendingMagicLink(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: targetEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback?type=magiclink`,
        },
      });
      if (error) throw error;
      toast.success('Magic sign-in link sent. Please check your email.');
    } catch (err: any) {
      console.error('Failed to send magic link:', err);
      toast.error(err?.message || 'Failed to send magic link');
    } finally {
      setSendingMagicLink(false);
    }
  };
  
  if (loading) {
    return (
      <div className="container mx-auto p-6 text-center">
        <div className="max-w-lg mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded mb-6"></div>
            <div className="h-4 bg-gray-200 rounded mb-2 w-3/4 mx-auto"></div>
            <div className="h-4 bg-gray-200 rounded mb-8 w-1/2 mx-auto"></div>
          </div>
          <p className="text-lg">Verifying your purchase...</p>
        </div>
      </div>
    );
  }
  
  if (status === 'error' || status === 'missing') {
    return (
      <div className="container mx-auto p-6 text-center">
        <div className="max-w-lg mx-auto bg-white shadow-md rounded p-8">
          <div className="text-red-500 text-5xl mb-4">❌</div>
          <h1 className="text-2xl font-bold mb-4">Verification Error</h1>
          <p className="mb-6 text-gray-600">
            {status === 'missing' 
              ? 'We couldn\'t find your checkout session. This could happen if you navigated here directly.'
              : 'There was an issue verifying your purchase. Please contact our support team.'}
          </p>
          <Link to="/checkout/guest" className="bg-blue-600 text-white px-6 py-3 rounded inline-block">
            Try Again
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-10">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8 mb-10">
        <div className="text-center mb-8">
          <div className="text-green-500 text-6xl mb-4">✅</div>
          <h1 className="text-3xl font-bold mb-4">Your trial is active</h1>
          
          {email && (
            <p className="mb-4 text-gray-600">
              Your account is linked to <strong>{email}</strong>
            </p>
          )}
          
          <div className="border-t border-b border-gray-200 py-6 my-6">
            <h2 className="text-xl font-semibold mb-2">Next: set your password</h2>
            <p className="text-gray-600">
              To access the tools, you need to set a password and sign in.
            </p>
          </div>
        </div>

        <div className="max-w-xl mx-auto space-y-4">
          <button
            onClick={() => void sendPasswordSetupLink()}
            disabled={sendingSetup || !email}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded disabled:opacity-50"
          >
            {sendingSetup ? 'Sending…' : 'Send password setup link'}
          </button>

          <button
            onClick={() => void sendMagicLink()}
            disabled={sendingMagicLink || !email}
            className="w-full bg-white border border-blue-600 text-blue-700 px-6 py-3 rounded disabled:opacity-50"
          >
            {sendingMagicLink ? 'Sending…' : 'Email me a magic sign-in link'}
          </button>

          <button
            onClick={() => {
              const q = new URLSearchParams({
                redirect: '/onboarding/trial',
                ...(email ? { email } : {}),
              }).toString();
              navigate(`/login?${q}`);
            }}
            className="w-full border border-gray-300 text-gray-900 px-6 py-3 rounded"
          >
            I already set my password (Sign in)
          </button>

          <div className="text-sm text-gray-600 text-center">
            If you don’t see the email, check your spam folder or resend the link.
          </div>
        </div>
        
        <div className="mt-10 text-center">
          <Link to="/" className="text-blue-600 hover:underline">
            Return to Homepage
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
